import { formatPhoneNumber } from "@gaiaProfessionals/Common/Utilities/formatPhoneNumber";
import { CallbackType, ErrorObjectType, InputObjectType, validate } from "@gaiaProfessionals/Common/Utilities/validate";
import {
    FormErrorsStateType,
    interestedInGaiaHerbs,
    intialErrorFormStateLegacy,
    newCustomerValidatorConfig,
    practiceTypeOptions,
    recommendationsOptions,
    refererOptions,
    ValidatorConfigType,
} from "@gaiaProfessionals/Components/HCP/HCPCreateAccountNewFormValidatorConfig";
import addAccount, { AddAccountParameter } from "@gaiaProfessionals/Store/Accounts/Handlers/HCPAddAccount";
import { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import logger from "@insite/client-framework/Logger";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import validatePassword from "@insite/client-framework/Store/CommonHandlers/ValidatePassword";
import validateReCaptcha from "@insite/client-framework/Store/Components/ReCaptcha/Handlers/ValidateReCaptcha";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import signIn from "@insite/client-framework/Store/Context/Handlers/SignIn";
import {
    getCountriesDataView,
    getCurrentCountries,
} from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";
import loadCurrentCountries, {
    loadCurrentCountriesParameter,
} from "@insite/client-framework/Store/Data/Countries/Handlers/LoadCurrentCountries";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import { getCreateAccountReturnUrl } from "@insite/client-framework/Store/Pages/CreateAccount/CreateAccountSelectors";
import translate from "@insite/client-framework/Translate";
import { CountryModel, StateModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ReCaptcha from "@insite/content-library/Components/ReCaptcha";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Checkbox from "@insite/mobius/Checkbox";
import CheckboxGroup from "@insite/mobius/CheckboxGroup";
import FormField, { FormFieldStyle } from "@insite/mobius/FormField";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps, GridOffset } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps, GridItemStyle } from "@insite/mobius/GridItem";
import { IconPresentationProps } from "@insite/mobius/Icon";
import AlertTriangle from "@insite/mobius/Icons/AlertTriangle";
import Link from "@insite/mobius/Link";
import { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Select, { SelectPresentationProps, SelectProps } from "@insite/mobius/Select";
import TextArea from "@insite/mobius/TextArea";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { RefObject, useContext, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const countriesDataView = getCountriesDataView(state, loadCurrentCountriesParameter);
    return {
        accountSettings: getSettingsCollection(state).accountSettings,
        returnUrl: getCreateAccountReturnUrl(state),
        countries: getCurrentCountries(state),
        shouldLoadCountries: !countriesDataView.value && !countriesDataView.isLoading,
        signInUrl: getPageLinkByPageType(state, "SignInPage")?.url,
    };
};

const mapDispatchToProps = {
    addAccount,
    signIn,
    validatePassword,
    loadCurrentCountries,
    validateReCaptcha: makeHandlerChainAwaitable<{ location: string; recaptchaRef: RefObject<ReCAPTCHA> }, boolean>(
        validateReCaptcha,
    ),
    // validateReCaptcha: makeHandlerChainAwaitable<{}, boolean>(validateReCaptcha),
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

export interface CreateAccountStyles {
    form?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    centeringWrapper?: InjectableCss;
    mainGridContainer?: GridContainerProps;
    phoneGridContainer?: GridContainerProps;
    userGridItem?: GridItemProps;
    userGridContainer?: GridContainerProps;
    emailGridItem?: GridItemProps;
    emailTextField?: TextFieldPresentationProps;
    userNameGridItem?: GridItemProps;
    fullWidthGridItem?: GridItemProps;
    businessNameTextField?: TextFieldPresentationProps;
    firstNameGridItem?: GridItemProps;
    lastNameGridItem?: GridItemProps;
    userNameTextField?: TextFieldPresentationProps;
    firstNameTextField?: TextFieldPresentationProps;
    lastNameTextField?: TextFieldPresentationProps;
    passwordGridItem?: GridItemProps;
    passwordTextField?: TextFieldPresentationProps;
    passwordIcon?: IconPresentationProps;
    confirmPasswordGridItem?: GridItemProps;
    confirmPasswordTextField?: TextFieldPresentationProps;
    confirmPasswordIcon?: IconPresentationProps;
    practitionerNameGridItem?: GridItemProps;
    practitionerTypeGridItem?: GridItemProps;
    practitionerNameTextField?: TextFieldPresentationProps;
    practitionerTypeTextField?: SelectPresentationProps;
    stateGridItem?: GridItemProps;
    stateTextField?: SelectPresentationProps;
    countryGridItem?: GridItemProps;
    countryTextField?: SelectPresentationProps;
    phoneNumberGridItem?: GridItemProps;
    extGridItem?: GridItemProps;
    phoneNumberTextField?: TextFieldPresentationProps;
    countryCodeGridItem?: GridItemProps;
    countryCodeTextField?: TextFieldPresentationProps;
    interestedDrpGridItem?: GridItemProps;
    interestedDrpTextField?: SelectPresentationProps;
    recommendDrpGridItem?: GridItemProps;
    recommendDrpTextField?: SelectPresentationProps;
    sellProductsDrpGridItem?: GridItemProps;
    sellProductsDrpTextField?: SelectPresentationProps;
    learnAbtDrpGridItem?: GridItemProps;
    learnAbtDrpTextField?: SelectPresentationProps;
    schoolAttendInputGridItem?: GridItemProps;
    schoolAttendInputTextField?: TextFieldPresentationProps;
    subscriptionGridItem?: GridItemProps;
    submitErrorGridItem?: GridItemProps;
    submitErrorTitle?: TypographyProps;
    submitGridItem?: GridItemProps;
    reCaptchaGridItem?: GridItemProps;
    submitButton?: ButtonPresentationProps;
    requirementsGridItem?: GridItemProps;
    requirementsTitle?: TypographyProps;
    passwordRequirementsGridItem?: GridItemProps;
    passwordRequirementsGridContainer?: GridContainerProps;
    passwordGridContainer?: GridContainerProps;
    createAccountTitle?: TypographyProps;
    createAccountBoldTitle?: TypographyProps;
    createAccountmailTitle?: TypographyProps;
    interestedGridItem?: GridItemProps;
    interestedLabel?: TypographyProps;
    recommendGridItem?: GridItemProps;
    recommendLabel?: TypographyProps;
    sellProductsGridItem?: GridItemProps;
    sellProductsLabel?: TypographyProps;
    schoolAttendGridItem?: GridItemProps;
    schoolAttendLabel?: TypographyProps;
    conferencesGridItem?: GridItemProps;
    conferencesLabel?: TypographyProps;
    learnAbtGridItem?: GridItemProps;
    learnAbtLabel?: TypographyProps;
    articlesGridItem?: GridItemProps;
    articlesLabel?: TypographyProps;
}
export const createAccountStyles: CreateAccountStyles = {
    createAccountTitle: {
        variant: "p",
        css: css`
            color: ${getColor("primary")};
            margin-bottom: 2rem !important;
        `,
    },
    createAccountBoldTitle: {
        css: css`
            color: ${getColor("primary")};
            font-weight: 900;
        `,
    },
    createAccountmailTitle: {
        variant: "p",
        css: css`
            color: ${getColor("common.accent")};
            display: inline-block;
            font-weight: 700;
            margin-bottom: 0;
        `,
    },
    centeringWrapper: {
        css: css`
            display: flex;
            height: 300px;
            justify-content: center;
            align-items: center;
        `,
    },
    mainGridContainer: {
        css: css`
            margin: 0 auto;
            > ${GridOffset} {
                justify-content: center;
                margin: 0 0 40px 0;
                width: 100%;
                padding: 0 20px;
                display: flex;
                > ${GridItemStyle} {
                    flex-basis: 60%;
                    ${({ theme }: { theme: BaseTheme }) =>
                        breakpointMediaQueries(theme, [
                            css`
                                flex-basis: 100%;
                            `,
                            css`
                                flex-basis: 100%;
                            `,
                            css`
                                flex-basis: 80%;
                            `,
                            css`
                                flex-basis: 80%;
                            `,
                            css`
                                flex-basis: 60%;
                            `,
                        ])}
                }
            }
        `,
    },
    phoneGridContainer: {
        css: css`
            > ${GridOffset} {
                /* justify-content: center; */
                margin: 0px;
                width: 100%;
                padding: 0px;
                display: flex;
                > ${GridItemStyle} {
                    flex-basis: 100%;
                    padding: 0px 15px 0px 0px;
                }
            }
        `,
    },
    userGridItem: {
        width: [12, 12, 6, 6, 6],
        css: css`
            flex-direction: column;
            background: #f0f4f3;
            border: #e5e5e5;
            max-width: 100% !important;
            padding: 40px 50px;
            border-radius: 8px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        width: 100%;
                        padding: 20px 15px;
                    `,
                    css`
                        width: 100%;
                        padding: 20px 15px;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    userGridContainer: {
        css: css`
            margin: 0 auto;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            max-width: 100%;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    emailGridItem: {
        width: [12, 12, 12, 6, 6],
    },
    fullWidthGridItem: {
        width: [12, 12, 12, 12, 12],
    },
    firstNameGridItem: {
        width: [12, 12, 12, 6, 6],
    },
    lastNameGridItem: {
        width: [12, 12, 12, 6, 6],
    },
    userNameGridItem: {
        width: [12, 12, 12, 6, 6],
    },
    passwordGridItem: {
        width: [12, 12, 12, 6, 6],
    },
    confirmPasswordGridItem: {
        width: [12, 12, 12, 6, 6],
    },
    practitionerNameGridItem: {
        width: [12, 12, 12, 6, 6],
    },
    phoneNumberGridItem: {
        width: [12, 12, 12, 6, 6],
        css: css`
            padding-right: 15px;
        `,
    },
    extGridItem: {
        width: [12, 12, 12, 4, 4],
        css: css`
            max-width: 110px !important;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin-top: 10px !important;
                    `,
                    css`
                        margin-top: 10px !important;
                    `,
                    css`
                        margin-top: 10px !important;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    countryCodeGridItem: {
        width: [12, 12, 12, 6, 6],
    },
    countryGridItem: {
        width: [12, 12, 12, 6, 6],
        css: css`
            > ${FormFieldStyle} {
                flex-direction: column;
                > ${TypographyStyle} {
                    text-align: left !important;
                    width: 100% !important;
                    padding: 0;
                    + div[class^="FormInputWrapper"] {
                        margin-top: 10px;
                        width: 100%;
                        > select {
                            height: 39.98px;
                        }
                    }
                }
            }
        `,
    },
    practitionerTypeGridItem: {
        width: [12, 12, 12, 6, 6],
        css: css`
            > ${FormFieldStyle} {
                flex-direction: column;
                > ${TypographyStyle} {
                    text-align: left !important;
                    width: 100% !important;
                    padding: 0;
                    + div[class^="FormInputWrapper"] {
                        margin-top: 10px;
                        width: 100%;
                        > select {
                            height: 39.98px;
                        }
                    }
                }
            }
        `,
    },
    stateGridItem: {
        width: [12, 12, 12, 6, 6],
        css: css`
            > ${FormFieldStyle} {
                flex-direction: column;
                > ${TypographyStyle} {
                    text-align: left !important;
                    width: 100% !important;
                    padding: 0;
                    + div[class^="FormInputWrapper"] {
                        margin-top: 10px;
                        width: 100%;
                        > select {
                            height: 39.98px;
                        }
                    }
                }
            }
        `,
    },
    interestedDrpGridItem: {
        width: [12, 12, 12, 6, 6],
        css: css`
            align-items: center;
            > ${FormFieldStyle} {
                flex-direction: column;
                > div[class^="FormInputWrapper"] {
                    text-align: left !important;
                    width: 100% !important;
                    padding: 0;
                    > select {
                        height: 39.98px;
                    }
                }
            }
        `,
    },
    recommendDrpGridItem: {
        width: [12, 12, 12, 6, 6],
        css: css`
            align-items: center;
            > ${FormFieldStyle} {
                flex-direction: column;
                > div[class^="FormInputWrapper"] {
                    text-align: left !important;
                    width: 100% !important;
                    padding: 0;
                    > select {
                        height: 39.98px;
                    }
                }
            }
        `,
    },
    sellProductsDrpGridItem: {
        width: [12, 12, 12, 6, 6],
        css: css`
            > ${FormFieldStyle} {
                flex-direction: column;
                > div[class^="FormInputWrapper"] {
                    text-align: left !important;
                    width: 100% !important;
                    padding: 0;
                    > select {
                        height: 39.98px;
                    }
                }
            }
        `,
    },
    learnAbtDrpGridItem: {
        width: [12, 12, 12, 6, 6],
        css: css`
            > ${FormFieldStyle} {
                flex-direction: column;
                > div[class^="FormInputWrapper"] {
                    text-align: left !important;
                    width: 100% !important;
                    padding: 0;
                    > select {
                        height: 39.98px;
                    }
                }
            }
        `,
    },
    interestedGridItem: {
        width: [12, 12, 12, 6, 6],
    },
    recommendGridItem: {
        width: [12, 12, 12, 6, 6],
    },
    sellProductsGridItem: {
        width: [12, 12, 12, 6, 6],
    },
    schoolAttendGridItem: {
        width: [12, 12, 12, 6, 6],
    },
    conferencesGridItem: {
        width: [12, 12, 12, 6, 6],
    },
    learnAbtGridItem: {
        width: [12, 12, 12, 6, 6],
    },
    schoolAttendInputGridItem: {
        width: [12, 12, 12, 6, 6],
    },
    articlesGridItem: {
        width: [12, 12, 12, 6, 6],
    },
    subscriptionGridItem: {
        width: 12,
        css: css`
            flex-wrap: wrap;
            padding-top: 0;
        `,
    },
    submitErrorGridItem: {
        width: [12, 12, 12, 6, 6],
    },
    submitErrorTitle: {
        color: "danger",
    },
    reCaptchaGridItem: { width: 12 },
    submitGridItem: {
        width: 12,
        css: css`
            justify-content: flex-start;
        `,
    },
    articlesLabel: {
        css: css`
            margin: 10px 0;
        `,
    },
    emailTextField: {
        cssOverrides: {
            inputSelect: css`
                padding: 0 2rem 0 10px !important;
                &:focus {
                    padding: 0 2rem 0 10px !important;
                }
            `,
        },
    },
};

const styles = createAccountStyles;
const StyledForm = getStyledWrapper("form");

const stateRegexp = /^[A-Za-z]+$/;

const CreateAccount = ({
    addAccount,
    validateReCaptcha,
    loadCurrentCountries,
    accountSettings,
    shouldLoadCountries,
    countries,
    history,
    signInUrl,
}: Props) => {
    const businessNameRequiredFieldMessage = siteMessage("Business name is Required");
    const firstNameRequiredFieldMessage = siteMessage("First name is Required");
    const lastNameRequiredFieldMessage = siteMessage("Last name is Required");
    const emailRequiredFieldMessage = siteMessage("CreateNewAccountInfo_EmailAddress_Required");
    const countryRequiredFieldMessage = siteMessage("Country is Required");
    const phoneNumberRequiredFieldMessage = siteMessage("Phone number is Required");
    const stateRequiredFieldMessage = siteMessage("State is Required");

    const practionerTypeRequiredFieldMessage = siteMessage("Practioner Type is Required");
    const questionRequiredFieldMessage = siteMessage("This field is Required");

    const [formErrors, setFormErrors] = React.useState<FormErrorsStateType>(intialErrorFormStateLegacy);
    const [isButtonDisabled, setIsButtonDisabled] = React.useState(true);
    const [businessName, setBusinessName] = useState("");
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [phoneNumber, setPhoneNumber] = useState("");
    const [otherState, setOtherState] = useState<string | undefined>("");
    const [businessNameError, setBusinessNameError] = useState(
        accountSettings.useEmailAsUserName ? "" : businessNameRequiredFieldMessage,
    );
    const [firstNameError, setFirstNameError] = useState(
        accountSettings.useEmailAsUserName ? "" : firstNameRequiredFieldMessage,
    );
    const [lastNameError, setLastNameError] = useState(
        accountSettings.useEmailAsUserName ? "" : lastNameRequiredFieldMessage,
    );
    const [countryError, setCountryError] = useState(
        accountSettings.useEmailAsUserName ? "" : countryRequiredFieldMessage,
    );
    const [stateError, setStateError] = useState(accountSettings.useEmailAsUserName ? "" : stateRequiredFieldMessage);
    const [phoneNumberError, setPhoneNumberError] = useState(
        accountSettings.useEmailAsUserName ? "" : phoneNumberRequiredFieldMessage,
    );

    const [email, setEmail] = useState("");
    const [selectedCountry, setSelectedCountry] = useState<CountryModel | undefined>(undefined);
    const [selectedState, setSelectedState] = useState<StateModel | undefined>(undefined);
    const [errorMessage, setErrorMessage] = useState("");
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [emailError, setEmailError] = useState(emailRequiredFieldMessage);
    const toasterContext = useContext(ToasterContext);

    const [practiceName, setPracticeName] = useState("");
    const [practiceType, setPracticeType] = useState("");
    const [q1, setQ1] = useState("");
    const [q2, setQ2] = useState("");
    const [q3, setQ3] = useState("");
    const [q4, setQ4] = useState("");
    const [q5, setQ5] = useState("");
    const [q6, setQ6] = useState("");
    const [phoneExtension, setPhoneExtension] = useState("");
    const [notification, setNotification] = useState(true);
    const recaptchaRef: RefObject<ReCAPTCHA> = React.createRef();

    const [practionerTypeError, setPractionerTypeError] = useState(practionerTypeRequiredFieldMessage);
    const [q1Error, setQ1Error] = useState(questionRequiredFieldMessage);
    const [q2Error, setQ2Error] = useState(questionRequiredFieldMessage);
    const [q3Error, setQ3Error] = useState(questionRequiredFieldMessage);
    const [q4Error, setQ4Error] = useState(questionRequiredFieldMessage);
    const [q5Error, setQ5Error] = useState(questionRequiredFieldMessage);
    const [q6Error, setQ6Error] = useState(questionRequiredFieldMessage);
    const [stateValidationError, setStateValidationError] = useState(false);

    const { useEmailAsUserName } = accountSettings;

    React.useEffect(() => {
        if (shouldLoadCountries) {
            loadCurrentCountries();
        }
    }, []);

    React.useEffect(() => {
        setIsButtonDisabled(validateSubmitDisabled(formErrors));
    }, [formErrors]);

    const resetForm = () => {
        setBusinessName("");
        setBusinessNameError("");
        setFirstName("");
        setFirstNameError("");
        setLastName("");
        setLastNameError("");
        setPhoneNumber("");
        setPhoneNumberError("");
        setEmail("");
        setEmailError("");
        setSelectedCountry(undefined);
        setCountryError("");
        setSelectedState(undefined);
        setStateError("");
    };

    const validateSubmitDisabled = (errorsState: FormErrorsStateType) => {
        return Object.keys(errorsState).length !== 0;
    };

    const validateHandler = (
        input: HTMLInputElement | HTMLSelectElement | any,
        customValidation?: CallbackType,
    ): void => {
        const inputObject: InputObjectType = {
            name: input.name,
            value: input.value,
        };
        const validatorConfig = newCustomerValidatorConfig[input.name as keyof ValidatorConfigType];
        const isValidate: ErrorObjectType | undefined = validate(inputObject, validatorConfig, customValidation);

        if (isValidate === undefined) {
            delete formErrors[input.name as keyof FormErrorsStateType];
            setFormErrors(formErrors);
        } else {
            setFormErrors({
                ...formErrors,
                [input.name]: isValidate ? isValidate.errorMsg : isValidate,
            });
        }
        if (!validatorConfig.required && input.value === "") {
            delete formErrors[input.name as keyof FormErrorsStateType];
            setFormErrors(formErrors);
        }

        setIsButtonDisabled(validateSubmitDisabled(formErrors));
    };

    const handelEmptyStateAfterCountryChanged = () => {
        setSelectedState(undefined);
        setOtherState("");
        setFormErrors({ ...formErrors, state: "" });
    };

    const handleCountryChange: SelectProps["onChange"] = event => {
        const country = countries?.find(c => c.id === event.target.value);
        validateHandler(event.target);
        setSelectedCountry(country);
        handelEmptyStateAfterCountryChanged();
    };

    const handleStateChange: SelectProps["onChange"] = event => {
        const state = selectedCountry?.states?.find(s => s.id === event.target.value);
        validateHandler(event.target);
        setSelectedState(state);
    };
    const otherStateChangeHandler = (input: HTMLInputElement) => {
        const regex = !stateRegexp.test(input.value);
        setStateValidationError(regex);
        validateHandler(input);
        setOtherState(input.value);
    };

    const validateSubmitEnabled = () => {
        return !(
            emailError ||
            businessName ||
            firstNameError ||
            lastNameError ||
            countryError ||
            phoneNumberError ||
            stateError
        );
    };

    const businessNameChangeHandler = (input: HTMLInputElement) => {
        validateHandler(input);
        setBusinessName(input.value);
    };
    const practiceNameChangeHandler = (input: HTMLInputElement) => {
        validateHandler(input);
        setPracticeName(input.value);
    };
    const practionerTypeChangeHandler: SelectProps["onChange"] = event => {
        validateHandler(event.target);
        setPracticeType(event.target.value);
    };
    const question1ChangeHandler: SelectProps["onChange"] = event => {
        validateHandler(event.target);
        setQ1(event.target.value);
    };
    const question2ChangeHandler: SelectProps["onChange"] = event => {
        validateHandler(event.target);
        setQ2(event.target.value);
    };
    const question3ChangeHandler: SelectProps["onChange"] = event => {
        validateHandler(event.target);
        setQ3(event.target.value);
    };
    const question4ChangeHandler = (input: HTMLInputElement) => {
        validateHandler(input);
        setQ4(input.value);
    };
    const question5ChangeHandler = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        // No need to validate conference attended field
        // validateHandler(event.target);
        setQ5(event.target.value);
    };
    const question6ChangeHandler: SelectProps["onChange"] = event => {
        validateHandler(event.target);
        setQ6(event.target.value);
    };
    const phoneExtensionChangeHandler = (input: HTMLInputElement) => {
        validateHandler(input);
        setPhoneExtension(input.value);
    };
    const notificationChangeHandler = (event: React.SyntheticEvent, value: boolean) => {
        // validateHandler(input);
        setNotification(value);
    };
    const firstNameChangeHandler = (input: HTMLInputElement) => {
        validateHandler(input);
        setFirstName(input.value);
    };

    const lastNameChangeHandler = (input: HTMLInputElement) => {
        validateHandler(input);
        setLastName(input.value);
    };

    const emailChangeHandler = (input: HTMLInputElement) => {
        validateHandler(input);
        setEmail(input.value);
    };

    const phoneNumberChangeHandler = (input: HTMLInputElement) => {
        validateHandler(input);
        setPhoneNumber(formatPhoneNumber(input.value));
    };

    const onSuccessHandler = () => {
        setErrorMessage("");
        setIsButtonDisabled(true);
        // resetForm();
        toasterContext.addToast({
            body: siteMessage("CreateAccount_SuccessMessage"),
            messageType: "success",
            timeoutLength: 3500,
        });
        // setTimeout(() => {
        //     signIn({
        //         // businessName
        //         // firstName,
        //         // lastName,
        //         userName: useEmailAsUserName ? email : userName,
        //         password,
        //         rememberMe: false,
        //         returnUrl,
        //     });
        // }, 500);

        setTimeout(() => {
            if (!signInUrl) {
                logger.warn("No url was found for SignInPage, defaulting to /SignIn");

                history.push("/SignIn");
            } else {
                history.push(`${signInUrl}`);
            }
        }, 3500);
    };

    const submitHandler = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setIsButtonDisabled(true);
        // if (!isSubmitted) {
        //     setIsSubmitted(true);
        // }

        // if (!validateSubmitEnabled()) {
        //     return;
        // }

        const isReCaptchaValid = await validateReCaptcha({ location: "CreateAccount", recaptchaRef });
        if (!isReCaptchaValid) {
            setIsButtonDisabled(false);
            return;
        }

        await addAccount({
            firstName,
            lastName,
            phoneNumber,
            extension: phoneExtension,
            q1,
            q2,
            q3,
            q4,
            q5,
            q6,
            practiceName,
            practiceType,
            notification,
            email,
            state: selectedState?.abbreviation || otherState,
            country: selectedCountry?.abbreviation,
            onSuccess: onSuccessHandler,
            onError: (error: any) => {
                setIsButtonDisabled(false);
                setErrorMessage(error);
                setIsSubmitting(false);
            },
            onComplete(resultProps: any) {
                if (resultProps.apiResult?.successful) {
                    // "this" is targeting the object being created, not the parent SFC
                    // eslint-disable-next-line react/no-this-in-sfc
                    (this as unknown as any)?.onSuccess?.();
                } else if (resultProps.apiResult?.errorMessage) {
                    // "this" is targeting the object being created, not the parent SFC
                    // eslint-disable-next-line react/no-this-in-sfc
                    (this as unknown as any)?.onError?.(resultProps.apiResult.errorMessage);
                }
            },
            isLegacyApi: true,
        } as unknown as AddAccountParameter);
    };

    return (
        // For phase 1 some fields were not required so they are commented below

        <StyledForm {...styles.form} onSubmit={submitHandler} noValidate>
            <GridContainer {...styles.mainGridContainer}>
                <GridItem {...styles.userGridItem}>
                    <GridContainer {...styles.userGridContainer}>
                        <GridItem {...styles.firstNameGridItem}>
                            <TextField
                                {...styles.firstNameTextField}
                                label={translate("First Name")}
                                name="firstName"
                                value={firstName}
                                data-test-selector="createAccount_firstName"
                                onChange={e => firstNameChangeHandler(e.currentTarget)}
                                onBlur={e => validateHandler(e.currentTarget)}
                                error={!!formErrors.firstName && formErrors.firstName}
                                iconProps={{
                                    color: "danger",
                                    src: formErrors.firstName ? AlertTriangle : undefined,
                                }}
                                placeholder="Enter first name"
                                required
                            />
                        </GridItem>
                        <GridItem {...styles.lastNameGridItem}>
                            <TextField
                                {...styles.lastNameTextField}
                                label={translate("Last Name")}
                                name="lastName"
                                value={lastName}
                                data-test-selector="createAccount_lastName"
                                onChange={e => lastNameChangeHandler(e.currentTarget)}
                                onBlur={e => validateHandler(e.currentTarget)}
                                error={!!formErrors.lastName && formErrors.lastName}
                                iconProps={{
                                    color: "danger",
                                    src: formErrors.lastName ? AlertTriangle : undefined,
                                }}
                                placeholder="Enter last name"
                                required
                            />
                        </GridItem>
                        <GridItem {...styles.fullWidthGridItem}>
                            <GridContainer {...styles.phoneGridContainer}>
                                <GridItem {...styles.phoneNumberGridItem}>
                                    <TextField
                                        {...styles.phoneNumberTextField}
                                        label={translate("Phone Number")}
                                        name="phoneNumber"
                                        value={phoneNumber}
                                        data-test-selector="createAccount_phoneNumber"
                                        onChange={e => phoneNumberChangeHandler(e.currentTarget)}
                                        onBlur={e => validateHandler(e.currentTarget)}
                                        error={!!formErrors.phoneNumber && formErrors.phoneNumber}
                                        iconProps={{
                                            color: "danger",
                                            src: formErrors.phoneNumber ? AlertTriangle : undefined,
                                        }}
                                        placeholder="Enter Phone Number"
                                        required
                                    />
                                </GridItem>
                                <GridItem {...styles.extGridItem}>
                                    <TextField
                                        {...styles.phoneNumberTextField}
                                        label={translate("Ext.")}
                                        name="extension"
                                        value={phoneExtension}
                                        data-test-selector="createAccount_phoneNumberExt"
                                        onChange={e => phoneExtensionChangeHandler(e.currentTarget)}
                                        placeholder="Enter Ext."
                                    />
                                </GridItem>
                            </GridContainer>
                        </GridItem>
                        <GridItem {...styles.fullWidthGridItem}>
                            <TextField
                                {...styles.emailTextField}
                                type="email"
                                label={translate("Email Address")}
                                name="email"
                                onChange={e => emailChangeHandler(e.currentTarget)}
                                onBlur={e => validateHandler(e.currentTarget)}
                                value={email}
                                data-test-selector="createAccount_email"
                                placeholder="Enter your email address"
                                required
                                error={!!formErrors.email && formErrors.email}
                                iconProps={{
                                    color: "danger",
                                    src: formErrors.email ? AlertTriangle : undefined,
                                }}
                            />
                        </GridItem>
                        <GridItem {...styles.lastNameGridItem}>
                            <TextField
                                {...styles.lastNameTextField}
                                label={translate("Practice Name")}
                                name="practiceName"
                                value={practiceName}
                                data-test-selector="createAccount_practiceName"
                                onChange={e => practiceNameChangeHandler(e.currentTarget)}
                                onBlur={e => validateHandler(e.currentTarget)}
                                error={!!formErrors.practiceName && formErrors.practiceName}
                                required
                                iconProps={{
                                    color: "danger",
                                    src: formErrors.practiceName ? AlertTriangle : undefined,
                                }}
                                placeholder="Enter practice name"
                            />
                        </GridItem>
                        <GridItem {...styles.lastNameGridItem}>
                            <Select
                                {...styles.countryTextField}
                                label="Practitioner Type"
                                name="practiceType"
                                onChange={practionerTypeChangeHandler}
                                onBlur={e => validateHandler(e.currentTarget)}
                                value={practiceType || ""}
                                error={!!formErrors.practiceType && formErrors.practiceType}
                                required
                            >
                                <option value="">{translate("Choose")}</option>
                                {practiceTypeOptions.map(pType => (
                                    <option key={pType.value} value={pType.value}>
                                        {pType.label}
                                    </option>
                                ))}
                            </Select>
                        </GridItem>
                        <GridItem {...styles.countryGridItem}>
                            <Select
                                {...styles.countryTextField}
                                label="Country"
                                name="country"
                                onChange={handleCountryChange}
                                onBlur={e => validateHandler(e.currentTarget)}
                                value={selectedCountry?.id || ""}
                                error={!!formErrors.country && formErrors.country}
                                labelPosition="left"
                                required
                            >
                                <option value="">{translate("Select Country")}</option>
                                {countries?.map(co => (
                                    <option key={co.id} value={co.id}>
                                        {co.name}
                                    </option>
                                ))}
                            </Select>
                        </GridItem>
                        <GridItem {...styles.stateGridItem}>
                            {selectedCountry?.states?.length === 0 ? (
                                <TextField
                                    {...styles.phoneNumberTextField}
                                    label={translate("State/Province")}
                                    name="state"
                                    onChange={e => otherStateChangeHandler(e.currentTarget)}
                                    onBlur={e => validateHandler(e.currentTarget)}
                                    error={
                                        // eslint-disable-next-line no-extra-boolean-cast
                                        !!formErrors.state
                                            ? formErrors.state
                                            : stateValidationError
                                            ? translate("Please Enter valid state")
                                            : undefined
                                    }
                                    value={otherState}
                                    data-test-selector="createAccount_firstName"
                                    placeholder="Enter state/Province"
                                    required
                                />
                            ) : (
                                <Select
                                    {...styles.stateTextField}
                                    label="State"
                                    name="state"
                                    value={selectedState?.id || ""}
                                    onChange={handleStateChange}
                                    onBlur={e => validateHandler(e.currentTarget)}
                                    error={!!formErrors.state && formErrors.state}
                                    labelPosition="left"
                                    required
                                >
                                    <option value="">{translate("Select State")}</option>

                                    {selectedCountry?.states?.map(st => (
                                        <option key={st.id} value={st.id}>
                                            {st.name}
                                        </option>
                                    ))}
                                </Select>
                            )}
                        </GridItem>
                        <GridItem {...styles.fullWidthGridItem}>
                            <Select
                                {...styles.countryTextField}
                                label="Are you interested in herbs for personal use or to recommend to patients/clients?"
                                name="q1"
                                onChange={question1ChangeHandler}
                                onBlur={e => validateHandler(e.currentTarget)}
                                value={q1 || ""}
                                error={!!formErrors.q1 && formErrors.q1}
                                required
                            >
                                <option value="">{translate("Choose")}</option>
                                {interestedInGaiaHerbs.map(pType => (
                                    <option key={pType.value} value={pType.value}>
                                        {pType.label}
                                    </option>
                                ))}
                            </Select>
                        </GridItem>
                        <GridItem {...styles.fullWidthGridItem}>
                            <Select
                                {...styles.countryTextField}
                                label="If you recommend herbs to patients/clients, how do they buy the herbs you recommend? Or if you are not currently using herbs with patients/clients, choose the option below that describes your intention for how to get herbs to them in the future."
                                name="q2"
                                onChange={question2ChangeHandler}
                                onBlur={e => validateHandler(e.currentTarget)}
                                value={q2 || ""}
                                error={!!formErrors.q2 && formErrors.q2}
                                required
                            >
                                <option value="">{translate("Choose")}</option>
                                {recommendationsOptions.map(pType => (
                                    <option key={pType.value} value={pType.value}>
                                        {pType.label}
                                    </option>
                                ))}
                            </Select>
                        </GridItem>
                        <GridItem {...styles.lastNameGridItem}>
                            <Select
                                {...styles.countryTextField}
                                label="Do you currently sell Gaia Herbs products?"
                                name="q3"
                                onChange={question3ChangeHandler}
                                onBlur={e => validateHandler(e.currentTarget)}
                                value={q3 || ""}
                                error={!!formErrors.q3 && formErrors.q3}
                                required
                            >
                                <option value="">{translate("Choose")}</option>
                                <option value="Yes">{translate("Yes")}</option>
                                <option value="No">{translate("No")}</option>
                            </Select>
                        </GridItem>
                        <GridItem {...styles.lastNameGridItem}>
                            <TextField
                                {...styles.lastNameTextField}
                                label={translate("School Attended")}
                                name="q4"
                                value={q4}
                                data-test-selector="createAccount_schoolAttended"
                                onChange={e => question4ChangeHandler(e.currentTarget)}
                                onBlur={e => validateHandler(e.currentTarget)}
                                error={!!formErrors.q4 && formErrors.q4}
                                iconProps={{
                                    color: "danger",
                                    src: formErrors.q4 ? AlertTriangle : undefined,
                                }}
                                placeholder="Enter school attended"
                                required
                            />
                        </GridItem>
                        <GridItem {...styles.fullWidthGridItem}>
                            <TextArea
                                label={translate("If you attend conferences, please list them here.")}
                                name="q5"
                                value={q5 || ""}
                                onChange={question5ChangeHandler}
                            />
                        </GridItem>
                        <GridItem {...styles.lastNameGridItem}>
                            <Select
                                {...styles.countryTextField}
                                label="How did you learn about us?"
                                name="q6"
                                onChange={question6ChangeHandler}
                                onBlur={e => validateHandler(e.currentTarget)}
                                value={q6 || ""}
                                error={!!formErrors.q6 && formErrors.q6}
                                required
                            >
                                <option value="">{translate("Choose")}</option>
                                {refererOptions.map(pType => (
                                    <option key={pType.value} value={pType.value}>
                                        {pType.label}
                                    </option>
                                ))}
                            </Select>
                        </GridItem>
                        <GridItem {...styles.fullWidthGridItem}>
                            <Checkbox
                                onChange={notificationChangeHandler}
                                checked={notification}
                                uid="email_notification"
                            >
                                {translate(
                                    "Receive emails regarding new research, published articles, new products, or conferences/lectures?",
                                )}
                            </Checkbox>
                        </GridItem>
                        <GridItem {...styles.subscriptionGridItem}>
                            <Typography {...styles.articlesLabel}>
                                {translate(
                                    "An email confirmation will be sent to you once your application has been approved.",
                                )}
                            </Typography>
                            <Typography {...styles.articlesLabel}>
                                {translate(
                                    "After you click “Submit”, you will be asked to send in your accredation (email/fax). All the details will be emailed to you.",
                                )}
                            </Typography>
                            <Typography {...styles.articlesLabel}>
                                {translate("By submitting this application, you agree to the ")}
                                <Link href="/PrivacyPolicy">
                                    <Typography {...styles.createAccountmailTitle}>
                                        {translate(" Privacy Policy.")}
                                    </Typography>
                                </Link>
                            </Typography>
                        </GridItem>

                        {errorMessage && (
                            <GridItem data-test-selector="createAccount_ErrorMessage" {...styles.submitErrorGridItem}>
                                <Typography {...styles.submitErrorTitle}>{errorMessage}</Typography>
                            </GridItem>
                        )}
                        <GridItem {...styles.reCaptchaGridItem}>
                            <ReCaptcha location="CreateAccount" recaptchaRef={recaptchaRef} />
                            {/* <ReCaptcha location="CreateAccount" /> */}
                        </GridItem>
                        <GridItem {...styles.submitGridItem}>
                            <Button
                                {...styles.submitButton}
                                type="submit"
                                disabled={isButtonDisabled}
                                data-test-selector="createAccount_createButton"
                            >
                                {translate("Submit Application")}
                            </Button>
                        </GridItem>
                    </GridContainer>
                </GridItem>
            </GridContainer>
        </StyledForm>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(CreateAccount)),
    definition: {
        allowedContexts: ["CreateAccountPage"],
        group: "Create Account",
        displayName: "HCP Create Account",
    },
};

export default widgetModule;
